
import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MapPin, Clock, User, Car, Zap } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";

interface AddressShare {
  id: string;
  sender_id: string;
  receiver_id: string;
  address: string;
  additional_info: string | null;
  shared_at: string;
  expires_at: string;
  is_active: boolean;
  sender_name?: string;
}

interface ReceivedAddressWidgetProps {
  otherUserId?: string;
}

const ReceivedAddressWidget = ({ otherUserId }: ReceivedAddressWidgetProps) => {
  const { user } = useAuth();
  const [receivedAddresses, setReceivedAddresses] = useState<AddressShare[]>([]);
  const [timeLeft, setTimeLeft] = useState<{ [key: string]: number }>({});

  const loadReceivedAddresses = async () => {
    if (!user?.id) {
      console.log('🔍 [ReceivedAddressWidget] No user ID, skipping load');
      return;
    }

    console.log('🔍 [ReceivedAddressWidget] Loading addresses for user:', user.id, 'from otherUser:', otherUserId);

    try {
      let query = supabase
        .from('address_shares')
        .select(`
          id,
          sender_id,
          receiver_id,
          address,
          additional_info,
          shared_at,
          expires_at,
          is_active
        `)
        .eq('receiver_id', user.id)
        .eq('is_active', true)
        .gt('expires_at', new Date().toISOString());

      // If we have a specific other user, filter for their addresses
      if (otherUserId) {
        query = query.eq('sender_id', otherUserId);
      }

      const { data, error } = await query.order('shared_at', { ascending: false });

      console.log('🔍 [ReceivedAddressWidget] Query result:', { data, error, receiverId: user.id, senderId: otherUserId });

      if (error) {
        console.error('❌ [ReceivedAddressWidget] Error loading received addresses:', error);
        return;
      }

      // Get sender names
      if (data && data.length > 0) {
        const senderIds = [...new Set(data.map(share => share.sender_id))];
        const { data: profiles } = await supabase
          .from('user_profiles')
          .select('user_id, display_name, first_name, last_name')
          .in('user_id', senderIds);

        const addressesWithNames = data.map(share => {
          const profile = profiles?.find(p => p.user_id === share.sender_id);
          const senderName = profile?.display_name || 
            `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || 
            'Unknown User';
          
          return {
            ...share,
            sender_name: senderName
          };
        });

        setReceivedAddresses(addressesWithNames);
        console.log('✅ [ReceivedAddressWidget] Set received addresses:', addressesWithNames);
      } else {
        setReceivedAddresses([]);
        console.log('📭 [ReceivedAddressWidget] No addresses found');
      }
    } catch (error) {
      console.error('❌ [ReceivedAddressWidget] Error loading received addresses:', error);
    }
  };

  const calculateTimeLeft = (expiresAt: string): number => {
    const now = new Date().getTime();
    const expiry = new Date(expiresAt).getTime();
    return Math.max(0, expiry - now);
  };

  const formatTimeLeft = (milliseconds: number): string => {
    const minutes = Math.floor(milliseconds / (1000 * 60));
    const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const openInMaps = (address: string) => {
    const encodedAddress = encodeURIComponent(address);
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    window.open(mapsUrl, '_blank');
  };

  const openInUber = (address: string) => {
    const encodedAddress = encodeURIComponent(address);
    const uberUrl = `uber://?action=setPickup&pickup=my_location&dropoff[formatted_address]=${encodedAddress}`;
    window.open(uberUrl, '_blank');
  };

  const openInBolt = (address: string) => {
    const encodedAddress = encodeURIComponent(address);
    const boltUrl = `bolt://riderequest?destination=${encodedAddress}`;
    window.open(boltUrl, '_blank');
  };

  useEffect(() => {
    loadReceivedAddresses();

    // Set up real-time subscription for new address shares
    const channel = supabase
      .channel('address_shares_updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'address_shares',
          filter: `receiver_id=eq.${user?.id}`
        },
        () => {
          loadReceivedAddresses();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, otherUserId]);

  useEffect(() => {
    if (receivedAddresses.length === 0) return;

    // Update timers every second
    const interval = setInterval(() => {
      const newTimeLeft: { [key: string]: number } = {};
      let hasExpired = false;

      receivedAddresses.forEach(share => {
        const remaining = calculateTimeLeft(share.expires_at);
        newTimeLeft[share.id] = remaining;
        if (remaining === 0) {
          hasExpired = true;
        }
      });

      setTimeLeft(newTimeLeft);

      // Reload if any address has expired
      if (hasExpired) {
        loadReceivedAddresses();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [receivedAddresses]);

  if (receivedAddresses.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      {receivedAddresses.map((share) => (
        <Card key={share.id} className="dark-pod border-electric-violet bg-carbon/80 backdrop-blur-glass">
          <CardContent className="p-6">
            <div className="mb-4">
              <div className="flex items-center space-x-3 mb-2">
                <User className="w-6 h-6 text-electric-violet" />
                <h3 className="text-xl font-semibold text-ice-white">
                  Address from {share.sender_name}
                </h3>
              </div>
              <div className="flex justify-center">
                <div className="flex items-center space-x-2 text-sm text-ice-white/70">
                  <Clock className="w-4 h-4" />
                  <span>{formatTimeLeft(timeLeft[share.id] || 0)}</span>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              <div>
                <p className="text-sm text-ice-white/70 mb-1">Address:</p>
                <p className="text-ice-white font-medium">{share.address}</p>
              </div>
              
              {share.additional_info && (
                <div>
                  <p className="text-sm text-ice-white/70 mb-1">Additional Info:</p>
                  <p className="text-ice-white">{share.additional_info}</p>
                </div>
              )}
              
              <div className="flex space-x-2">
                <Button
                  onClick={() => openInMaps(share.address)}
                  className="flex-1 bg-electric-violet hover:bg-electric-violet/80 text-ice-white font-semibold py-3 px-4 rounded-lg shadow-electric-violet hover:shadow-lg hover:scale-105 transition-all duration-300"
                >
                  <MapPin className="w-4 h-4 mr-1" />
                  Maps
                </Button>
                <Button
                  onClick={() => openInUber(share.address)}
                  className="flex-1 bg-carbon hover:bg-carbon/80 text-ice-white font-semibold py-3 px-4 rounded-lg border border-ice-white/20 hover:border-ice-white/40 hover:scale-105 transition-all duration-300"
                >
                  <Car className="w-4 h-4 mr-1" />
                  Uber
                </Button>
                <Button
                  onClick={() => openInBolt(share.address)}
                  className="flex-1 bg-[#34D186] hover:bg-[#2BC071] text-white font-semibold py-3 px-4 rounded-lg hover:shadow-lg hover:scale-105 transition-all duration-300"
                >
                  <Zap className="w-4 h-4 mr-1" />
                  Bolt
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default ReceivedAddressWidget;
